import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useVoice } from "../../contexts/VoiceContext";
import { VoiceAuroraOrb } from "./VoiceAuroraOrb";
import {
  clampDockPosition,
  handleDockPointerDown,
  measureDockVisualBounds,
  readContextEvent,
  readDockPosition,
  readLocalConsulControlPreview,
  responseForContext,
  responseScenarioIndex,
  stateLabel,
  type VoiceCaoContext,
  voiceCaoOrbState,
} from "./voice-cao-dock-helpers";
import {
  BottomContextDrawer,
  TopWaveDrawer,
  VoiceDockControls,
} from "./VoiceCaoDockPanels";
import { useVoiceDockMeter } from "../../voice-cao-dock-meter";
import { openVoiceCaoSidebarHandoff } from "../../voice-cao-sidebar-handoff";
import "../../styles/voice-cao-dock.css";

const DOCK_HIDE_DELAY_MS = 2500;
const CONTEXT_HIDE_DELAY_MS = 12_000;
const BOTTOM_DRAWER_EXIT_MS = 180;

export function VoiceCaoDock() {
  const {
    enabled,
    runtimeState,
    conversationId,
    lastUserText,
    lastAssistantText,
    hasUserRequestActivity,
    replyAudioEnabled,
    voiceWidgetHidden,
    setEnabled,
    setReplyAudioEnabled,
    setVoiceWidgetHidden,
    finishCurrentInput,
  } = useVoice();
  const [shouldRender, setShouldRender] = useState(enabled);
  const [actionContext, setActionContext] = useState<VoiceCaoContext | null>(
    null,
  );
  const shouldShowBottomDrawer =
    hasUserRequestActivity || actionContext !== null;
  const [isBottomDrawerMounted, setIsBottomDrawerMounted] = useState(
    shouldShowBottomDrawer,
  );
  const [isDismissedUntilNextEnable, setIsDismissedUntilNextEnable] =
    useState(false);
  const [isBottomExpanded, setIsBottomExpanded] = useState(false);
  const [dockPosition, setDockPosition] = useState(readDockPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dockStageRef = useRef<HTMLElement | null>(null);
  const wasVoiceEnabledRef = useRef(enabled);
  const isLocalConsulControlPreview = readLocalConsulControlPreview();
  const response = useMemo(
    () =>
      responseForContext(
        actionContext,
        isLocalConsulControlPreview,
        lastUserText,
        lastAssistantText,
      ),
    [
      actionContext,
      isLocalConsulControlPreview,
      lastAssistantText,
      lastUserText,
    ],
  );
  const isWidgetHidden = voiceWidgetHidden;
  const isProcessingRequest =
    runtimeState === "thinking" || actionContext !== null;
  const isAgentReplying = runtimeState === "speaking";
  const isTextReplyMode = !replyAudioEnabled;
  const meter = useVoiceDockMeter({
    userEnabled: enabled && runtimeState === "listening" && !isWidgetHidden,
    agentEnabled: isAgentReplying && !isTextReplyMode && !isWidgetHidden,
    responseMode: responseScenarioIndex(response),
  });
  const openSideChat = () =>
    openVoiceCaoSidebarHandoff({
      response,
      context: actionContext,
      conversationId,
      lastUserText,
      lastAssistantText,
    });
  const toggleReplyMode = () => setReplyAudioEnabled(isTextReplyMode);
  const dockOrbState = voiceCaoOrbState({
    runtimeState,
    shouldShowBottomDrawer,
  });

  useLayoutEffect(() => {
    const stage = dockStageRef.current;
    if (!stage) return;
    const keepDockInViewport = () => {
      const bounds = measureDockVisualBounds(stage);
      setDockPosition((position) => {
        const next = clampDockPosition(position, bounds);
        return next.x === position.x && next.y === position.y ? position : next;
      });
    };
    keepDockInViewport();
    window.addEventListener("resize", keepDockInViewport);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(keepDockInViewport);
    observer?.observe(stage);
    stage
      .querySelectorAll<HTMLElement>(
        ".voice-cao-top-drawer, .voice-cao-dock, .voice-cao-bottom-drawer",
      )
      .forEach((element) => observer?.observe(element));
    return () => {
      window.removeEventListener("resize", keepDockInViewport);
      observer?.disconnect();
    };
  }, [
    isBottomExpanded,
    response.kind,
    response.stream,
    shouldShowBottomDrawer,
  ]);

  useEffect(() => {
    const wasVoiceEnabled = wasVoiceEnabledRef.current;
    wasVoiceEnabledRef.current = enabled;
    if (enabled) {
      setIsDismissedUntilNextEnable(false);
      return;
    }
    if (wasVoiceEnabled && isWidgetHidden) {
      setIsDismissedUntilNextEnable(true);
      setVoiceWidgetHidden(false);
    }
    setReplyAudioEnabled(true);
    setIsBottomExpanded(false);
    setActionContext(null);
  }, [enabled, isWidgetHidden, setReplyAudioEnabled, setVoiceWidgetHidden]);

  useEffect(() => {
    if (isDismissedUntilNextEnable && !enabled && !actionContext) {
      setShouldRender(false);
      return;
    }
    if (
      enabled ||
      actionContext ||
      isLocalConsulControlPreview ||
      isWidgetHidden
    ) {
      setShouldRender(true);
      return;
    }
    const timer = window.setTimeout(
      () => setShouldRender(false),
      DOCK_HIDE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [
    actionContext,
    enabled,
    isDismissedUntilNextEnable,
    isLocalConsulControlPreview,
    isWidgetHidden,
  ]);

  useEffect(() => {
    const handleHiddenWidgetClose = () => {
      setIsDismissedUntilNextEnable(true);
      setVoiceWidgetHidden(false);
      setIsBottomExpanded(false);
      setActionContext(null);
      setShouldRender(false);
    };
    window.addEventListener(
      "fintheon:voice-widget-close",
      handleHiddenWidgetClose,
    );
    return () =>
      window.removeEventListener(
        "fintheon:voice-widget-close",
        handleHiddenWidgetClose,
      );
  }, [setVoiceWidgetHidden]);

  useEffect(() => {
    if (shouldShowBottomDrawer) {
      setIsBottomDrawerMounted(true);
      return;
    }
    if (!isBottomDrawerMounted) return;
    const timer = window.setTimeout(
      () => setIsBottomDrawerMounted(false),
      BOTTOM_DRAWER_EXIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isBottomDrawerMounted, shouldShowBottomDrawer]);

  useEffect(() => {
    if (!shouldShowBottomDrawer) {
      setIsBottomExpanded(false);
      return;
    }
    setIsBottomExpanded(true);
  }, [shouldShowBottomDrawer, response.kind]);

  useEffect(() => {
    if (!actionContext) return;
    const timer = window.setTimeout(
      () => setActionContext(null),
      CONTEXT_HIDE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [actionContext]);

  useEffect(() => {
    const handleContext = (event: Event) => {
      const context = readContextEvent(event);
      if (!context) return;
      setActionContext(context);
      setShouldRender(true);
    };
    const events = [
      "fintheon:consul-control-market-context",
      "fintheon:consul-control-read-visible",
      "fintheon:consul-control-agent-task",
      "fintheon:consul-control-chart-context",
    ];
    events.forEach((eventName) =>
      window.addEventListener(eventName, handleContext),
    );
    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, handleContext),
      );
    };
  }, []);

  if (!shouldRender) return null;

  return (
    <section
      ref={dockStageRef}
      className="voice-cao-integrated-stage fixed right-5 top-[72px] z-[92] select-none text-[var(--fintheon-text)]"
      data-voice-cao-dock="true"
      data-enabled={enabled ? "true" : "false"}
      data-dragging={isDragging ? "true" : "false"}
      data-widget-hidden={isWidgetHidden ? "true" : "false"}
      aria-hidden={isWidgetHidden ? "true" : undefined}
      style={
        {
          "--dock-w": "340px",
          "--voice-cao-primary":
            "var(--fintheon-primary, var(--fintheon-accent, #c79f4a))",
          width: "min(var(--dock-w), calc(100vw - 40px))",
          minHeight: 228,
          left: dockPosition.x,
          top: dockPosition.y,
          right: "auto",
        } as CSSProperties
      }
      aria-label="Voice CAO dock"
    >
      <div
        className="voice-cao-dock-wrap voice-cao-dock-wrap--integrated"
        data-text-reply={isTextReplyMode ? "true" : "false"}
        data-dragging={isDragging ? "true" : "false"}
        style={{ animation: "none" }}
      >
        <TopWaveDrawer
          volume={meter.volume}
          phase={meter.phase}
          speaker={meter.speaker}
          isProcessingRequest={meter.speaker !== "idle"}
          isTextReplyMode={isTextReplyMode}
          isSendEnabled={enabled && runtimeState === "listening"}
          onToggleTextReplyMode={toggleReplyMode}
          onStopVoice={() => setEnabled(false)}
          onSendVoiceInput={finishCurrentInput}
        />
        <div
          className="voice-cao-dock"
          onPointerDown={(event) =>
            handleDockPointerDown(
              event,
              setDockPosition,
              setIsDragging,
              () => setIsBottomExpanded(false),
              () => measureDockVisualBounds(dockStageRef.current),
            )
          }
        >
          <div
            className="voice-cao-orb-anchor"
            data-orb-state={dockOrbState}
            aria-hidden="true"
          >
            <VoiceAuroraOrb state={dockOrbState} />
          </div>
          <div className="voice-cao-copy">
            <strong>Harper</strong>
            <span>
              {isTextReplyMode
                ? "Muted - text"
                : isProcessingRequest
                  ? "Thinking..."
                  : stateLabel(runtimeState)}
            </span>
          </div>
          <VoiceDockControls
            isTextReplyMode={isTextReplyMode}
            onOpenSideChat={openSideChat}
            onToggleTextReplyMode={toggleReplyMode}
            onHideWidget={() => setVoiceWidgetHidden(true)}
          />
        </div>
        {isBottomDrawerMounted ? (
          <BottomContextDrawer
            response={response}
            isTextReplyMode={isTextReplyMode}
            isExpanded={isBottomExpanded}
            isVisible={shouldShowBottomDrawer}
            onExpandedChange={setIsBottomExpanded}
            onOpenSideChat={openSideChat}
          />
        ) : null}
      </div>
    </section>
  );
}
